/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IComponentStyles } from '@fluentui/foundation-legacy';
import { ActionButton } from '@fluentui/react/lib/Button';
import { Link } from '@fluentui/react/lib/Link';
import { Stack } from '@fluentui/react/lib/Stack';
import { ITextProps, ITextSlots } from '@fluentui/react/lib/Text';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText } from 'src/core/components/LuisText';
import { RBACTooltip } from 'src/core/components/RBACTooltip';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { QnaMakerPredictedAnswer } from 'src/qnaMaker/core/types/QnaMakerPredictedQnA';

type QnaMakerAnswerDetailsProps = {
    answer: QnaMakerPredictedAnswer;
    onEditQnA: (answerId: number) => void;
    isDefaultAnswer: boolean;
    isEditDisabled?: boolean;
    editTooltipContent?: string;
};

const messages = defineMessages({
    sourceLabel: { id: 'qna.qnaMakerAnswerDetails.sourceLabel', defaultMessage: 'Source' },
    answersTitle: { id: 'qna.qnaMakerAnswerDetails.answersTitle', defaultMessage: 'Answer' },
    defaultAnswersTitle: { id: 'qna.qnaMakerDetailedBlade.defaultAnswersTitle', defaultMessage: 'Default answer' },
    editQna: { id: 'qna.qnaMakerAnswerDetails.editQna', defaultMessage: 'Edit question answer pair' },
    answerSpanTextLabel: { id: 'qna.qnaMakerAnswerDetails.answerSpanTextLabel', defaultMessage: 'Answer-span text:' },
    confidenceScoreLabel: { id: 'qna.qnaMakerAnswerDetails.confidenceScoreLabel', defaultMessage: 'Confidence score:' },
    seeAdditionalInfo: { id: 'qna.qnaMakerAnswerDetails.seeAdditionalInfo', defaultMessage: 'See additional information' },
    hideAdditionalInfo: { id: 'qna.qnaMakerAnswerDetails.hideAdditionalInfo', defaultMessage: 'Hide additional information' },
    answerSpanConfidenceScoreLabel: {
        id: 'qna.qnaMakerAnswerDetails.answerSpanConfidenceScoreLabel',
        defaultMessage: 'Answer-span confidence score:'
    }
});

const longTextStyles = classNamesFunction<ITextProps, IComponentStyles<ITextSlots>>()({ root: { whiteSpace: 'break-spaces' } });

export const QnaMakerAnswerDetails = observer((props: QnaMakerAnswerDetailsProps) => {
    const { answer, onEditQnA, isDefaultAnswer, isEditDisabled = false, editTooltipContent } = props;
    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();

    const { userRole, displayedUserRole } = rootStore.serviceKey;
    const { canWrite } = mapUserRoleToUserActions(userRole);
    const { languageDirection } = rootStore.currentQnaMakerProjectStore;

    const [hideAdditionalInfo, setHideAdditionalInfo] = React.useState(true);

    return (
        <Stack tokens={{ childrenGap: 12 }}>
            <Stack horizontal verticalAlign="center" horizontalAlign="space-between">
                {answer.text.trim() ? (
                    <LuisText fontWeight={500}>{localize(isDefaultAnswer ? messages.defaultAnswersTitle : messages.answersTitle)}</LuisText>
                ) : (
                    <Stack>
                        <LuisText fontWeight={500}>{localize(messages.confidenceScoreLabel)}</LuisText>
                        <LuisText>{answer.score.toFixed(2)}</LuisText>
                    </Stack>
                )}
                <RBACTooltip
                    hidden={(canWrite || isDefaultAnswer) && !editTooltipContent}
                    displayedUserRole={displayedUserRole}
                    content={editTooltipContent}
                    data-automation-id="editQnaButtonLanguageToolTip"
                    componentSurroundedByToolTip={
                        <ActionButton
                            iconProps={{ iconName: 'Edit' }}
                            text={localize(messages.editQna)}
                            onClick={() => onEditQnA(answer?.id)}
                            disabled={!canWrite || isDefaultAnswer || isEditDisabled}
                        />
                    }
                />
            </Stack>

            {answer.text.trim() && (
                <>
                    <LuisText className="ck" dir={languageDirection} styles={longTextStyles}>
                        {answer.text}
                    </LuisText>

                    <Stack>
                        <LuisText fontWeight={500}>{localize(messages.confidenceScoreLabel)}</LuisText>
                        <LuisText>{answer.score.toFixed(2)}</LuisText>
                    </Stack>
                </>
            )}

            {!hideAdditionalInfo && (
                <Stack>
                    <Stack>
                        <LuisText fontWeight={500}>{localize(messages.sourceLabel)}</LuisText>
                        <LuisText styles={longTextStyles}>{answer.source || '--'}</LuisText>
                    </Stack>

                    {answer.answerSpan && [
                        <Stack>
                            <LuisText fontWeight={500}>{localize(messages.answerSpanTextLabel)}</LuisText>
                            <LuisText>{answer.answerSpan?.text}</LuisText>
                        </Stack>,
                        <Stack>
                            <LuisText fontWeight={500}>{localize(messages.answerSpanConfidenceScoreLabel)}</LuisText>
                            <LuisText>{answer.answerSpan.score?.toFixed(2)}</LuisText>
                        </Stack>
                    ]}
                </Stack>
            )}

            <Link onClick={() => setHideAdditionalInfo(!hideAdditionalInfo)}>
                {localize(hideAdditionalInfo ? messages.seeAdditionalInfo : messages.hideAdditionalInfo)}
            </Link>
        </Stack>
    );
});
